import { Navigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useAuthLoading } from '@/store';
import { DEFAULT_AUTHENTICATED_ROUTE } from './routes';
import { Box, CircularProgress } from '@mui/material';

interface PublicRouteProps {
  children: React.ReactNode;
  redirectAuthenticated?: boolean;
}

/**
 * Public route wrapper component
 * Optionally redirects authenticated users away from auth pages
 */
export const PublicRoute: React.FC<PublicRouteProps> = ({
  children,
  redirectAuthenticated = false,
}) => {
  const isAuthenticated = useIsAuthenticated();
  const isLoading = useAuthLoading();
  const location = useLocation();

  // Show loading spinner while checking auth status
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect authenticated users away from login/signup pages
  if (redirectAuthenticated && isAuthenticated) {
    // Check location.state first, then ?next= query param (used by email-link flows)
    const returnUrl =
      (location.state as { returnUrl?: string })?.returnUrl ||
      new URLSearchParams(location.search).get('next') ||
      DEFAULT_AUTHENTICATED_ROUTE;
    return <Navigate to={returnUrl} replace />;
  }

  // Render the public content
  return <>{children}</>;
};

export default PublicRoute;
